<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import {
  WheelsTimeInput,
  WheelsAddressPicker,
  WheelsDatePicker,
  WheelsButton,
} from '../../components'
import type {
  WheelsPlaceDetailStrategy,
  WheelsPickableAddress,
  WheelsSearchStrategy,
} from '../WheelsAddressPicker/WheelsAddressPicker.constants'
import {
  buildDateTime,
  dateFromDatetime,
  timeFromDatetime,
} from '../../util/timeStrings'
import { DateTime } from 'luxon'

type ItineraryItem = {
  dropoffDatetime?: string | null
  pickupDatetime?: string | null
  travelTimeFromPreviousStopInSeconds?: number | null
  address: WheelsPickableAddress
  stopId?: number
  stopChanged?: boolean
  keepOnSite?: boolean
}

const props = withDefaults(
  defineProps<{
    searchStrategy: WheelsSearchStrategy
    placeDetailStrategy: WheelsPlaceDetailStrategy
    itinerary: Array<ItineraryItem>
    allowAddStop?: boolean
    allowRemoveStop?: boolean
    allowKeepOnSite?: boolean
    allowPickupDropoffCustomization?: boolean
  }>(),
  {
    allowAddStop: true,
    allowRemoveStop: true,
    allowKeepOnSite: true,
    allowPickupDropoffCustomization: false,
  },
)

const itinerary = ref(props.itinerary)

const emit = defineEmits<{
  (e: 'update', itinerary: Array<ItineraryItem>): void
  (e: 'address-changed', index: number, address: WheelsPickableAddress): void
}>()

const stopErrors = computed(() =>
  itinerary.value.map((item, index) => getStopError(item, index)),
)

const hasStopError = (index: number) => Boolean(stopErrors.value[index])

watch(
  itinerary,
  (newItinerary) => {
    emit('update', newItinerary)
  },
  { deep: true },
)

onMounted(() => {
  while (itinerary.value.length < 2) {
    addEmptyStop()
  }
})

function addEmptyStop(index?: number) {
  const emptyStop = {
    address: {},
    pickupDatetime: null,
    dropoffDatetime: null,
    travelTimeFromPreviousStopInSeconds: null,
  }
  if (index === undefined) {
    itinerary.value.push(emptyStop)
  } else {
    itinerary.value.splice(index + 1, 0, emptyStop)
  }
}

function isFirstStop(index: number): boolean {
  return index === 0
}

function isLastStop(index: number): boolean {
  return index === itinerary.value.length - 1
}

function formatTravelTime(seconds: number | null): string {
  if (!seconds) return ''

  const minutes = Math.round(seconds / 60)
  if (minutes < 60) {
    return `${minutes} min`
  } else {
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
  }
}

const stopTypeLabel = (index: number) => {
  if (isFirstStop(index)) {
    return 'Pickup'
  }
  if (isLastStop(index)) {
    return 'Dropoff'
  }
  return 'Stop'
}

function handleRemoveStopClick(index: number) {
  itinerary.value.splice(index, 1)
}

function handleAddStopClick(index: number) {
  addEmptyStop(index)
}

function handleAddressUpdated(
  item: ItineraryItem,
  index: number,
  address: WheelsPickableAddress,
) {
  item.address = address
  emit('address-changed', index, address)
}


function getStopError(item: ItineraryItem, index: number): string | null {
  if (
    !item.dropoffDatetime ||
    !item.address?.zoneId ||
    index === 0
  ) {
    return null
  }

  const arrival = DateTime.fromISO(item.dropoffDatetime, {
    zone: item.address?.zoneId,
  })

  if (item.pickupDatetime) {
    const pickup = DateTime.fromISO(item.pickupDatetime, {
      zone: item.address?.zoneId,
    })
    if (pickup < arrival) {
      return `Pickup must be after the dropoff time of ${arrival.toFormat('h:mm a')} on ${arrival.toFormat('LL/dd/yyyy')}`
    }
  }
  

  if (index >= 1) {
    const previousStop = itinerary.value[index - 1]

    if (previousStop.pickupDatetime && previousStop.address?.zoneId) {
      const previousPickup = DateTime.fromISO(previousStop.pickupDatetime, {
        zone: previousStop.address.zoneId,
      })

      if (arrival < previousPickup) {
        return `Dropoff must be after the pickup time of the previous stop at ${previousPickup.toFormat('h:mm a')} on ${previousPickup.toFormat('LL/dd/yyyy')}`
      }
    }
  } else {
    console.log('No previous stop to compare against for stop index', index)
  }

  return null
}

function handlePickupTimeChange(item: ItineraryItem, newTime: string | null) {
  // TODO: It might make sense to use the user's timezone here?
  if (!item.address?.zoneId) {
    console.warn('Cannot set pickup time without timezone information')
    return
  }

  // Since we don't currently support clearing times, we won't allow empty values
  if (!newTime) {
    return
  }

  // Extract existing date, or default to today if no existing datetime
  const existingDate = item.pickupDatetime
    ? dateFromDatetime(item.pickupDatetime, item.address.zoneId)
    : DateTime.now().setZone(item.address.zoneId).toFormat('yyyy-MM-dd')

  try {
    // Build new complete datetime using 24-hour format
    const newPickupDatetime = buildDateTime(
      existingDate,
      newTime,
      item.address.zoneId,
    )

    item.pickupDatetime = newPickupDatetime
    emit('update', itinerary.value)
  } catch (error) {
    console.error('Error building datetime from time change:', error)
  }
}

function handlePickupDateChange(item: ItineraryItem, newDate: string) {
  if (!item.address?.zoneId) {
    console.warn('Cannot set pickup date without timezone information')
    return
  }

  // Extract existing time, or default to 12:00 if no existing datetime
  const existingTime = item.pickupDatetime
    ? timeFromDatetime(item.pickupDatetime, item.address.zoneId)
    : '12:00 PM'

  try {
    const newPickupDatetime = buildDateTime(
      newDate,
      existingTime,
      item.address.zoneId,
    )
    item.pickupDatetime = newPickupDatetime
    emit('update', itinerary.value)
  } catch (error) {
    console.error('Error building datetime from date change:', error)
  }
}

function handleDropoffTimeChange(item: ItineraryItem, newTime: string | null) {
  if (!item.address?.zoneId) {
    console.warn('Cannot set dropoff time without timezone information')
    return
  }

  if (!newTime) return

  const existingDate = item.dropoffDatetime
    ? dateFromDatetime(item.dropoffDatetime, item.address.zoneId)
    : DateTime.now().setZone(item.address.zoneId).toFormat('yyyy-MM-dd')

  try {
    item.dropoffDatetime = buildDateTime(existingDate, newTime, item.address.zoneId)
    emit('update', itinerary.value)
  } catch (error) {
    console.error('Error building datetime from time change:', error)
  }
}

function handleDropoffDateChange(item: ItineraryItem, newDate: string) {
  if (!item.address?.zoneId) {
    console.warn('Cannot set dropoff date without timezone information')
    return
  }

  const existingTime = item.dropoffDatetime
    ? timeFromDatetime(item.dropoffDatetime, item.address.zoneId)
    : '12:00 PM'

  try {
    item.dropoffDatetime = buildDateTime(newDate, existingTime, item.address.zoneId)
    emit('update', itinerary.value)
  } catch (error) {
    console.error('Error building datetime from date change:', error)
  }
}

function handleClearDropoffRow(item: ItineraryItem) {
  item.dropoffDatetime = null
  emit('update', itinerary.value)
}

function handleClearPickupRow(item: ItineraryItem) {
  item.pickupDatetime = null
  emit('update', itinerary.value)
}
</script>

<template>
  <div class="wheels-itinerary-editor">
    <ul>
      <li
        v-for="(item, index) in itinerary"
        :key="`wheels-itinerary-${item.stopId || index}`"
      >
        <div
          :key="`wheels-itinerary-item-${item.stopId || index}`"
        >
          <fieldset
            class="wheels-itinerary-editor__item-content"
            :class="{
              'wheels-itinerary-editor__item-content--error':
                hasStopError(index),
              'wheels-itinerary-editor__item-content--v2': allowPickupDropoffCustomization,
            }"
          >
            <div class="wheels-itinerary-editor__stop-header">
              <legend class="wheels-itinerary-editor__stop-type">
                <span class="wheels-itinerary-editor__stop-type-number">{{
                  index + 1
                }}</span>
                <span class="wheels-itinerary-editor__stop-typelabel">{{
                  stopTypeLabel(index)
                }}</span>
                <span
                  v-if="stopErrors[index]"
                  class="wheels-itinerary-editor__error"
                  role="alert"
                  :id="`itinerary-stop-error-${index}`"
                >
                  {{ stopErrors[index] }}
                </span>
              </legend>
            </div>

            <label class="wheels-itinerary-editor__address">
              <div class="wheels-itinerary-editor__address-picker-label">
                <span class="wheels-form-label">
                  {{ stopTypeLabel(index) }} Location
                  <template v-if="item.address && item.address.title">
                    - {{ item.address.title }}
                  </template>
                </span>
                <span
                  class="wheels-itinerary-editor__stop-travel-time"
                  :key="`travel-time-${index}`"
                  v-if="
                    !isFirstStop(index) &&
                    item.travelTimeFromPreviousStopInSeconds
                  "
                >
                  Travel:
                  {{
                    formatTravelTime(item.travelTimeFromPreviousStopInSeconds)
                  }}
                </span>
              </div>
              <WheelsAddressPicker
                class="wheels-itinerary-editor__address-picker"
                @change="handleAddressUpdated(item, index, $event)"
                :address="item.address"
                :searchStrategy="searchStrategy"
                :placeDetailStrategy="placeDetailStrategy"
              />
            </label>
            <template v-if="!allowPickupDropoffCustomization">
              <!-- Dropoff Date -->
              <label class="wheels-itinerary-editor__date" v-if="!isFirstStop(index)">
                <span class="wheels-form-label">Dropoff Date</span>
                <WheelsDatePicker
                  @change="handleDropoffDateChange(item, $event)"
                  class="wheels-form-field wheels-itinerary-editor__date-picker"
                  :date="
                    item.dropoffDatetime && item.address?.zoneId
                      ? dateFromDatetime(item.dropoffDatetime, item.address.zoneId)
                      : ''
                  "
                />
              </label>

              <!-- Dropoff Time -->
              <label class="wheels-itinerary-editor__time" v-if="!isFirstStop(index)">
                <span class="wheels-form-label">Dropoff Time</span>
                <WheelsTimeInput
                  @change="handleDropoffTimeChange(item, $event)"
                  :value="
                    item.dropoffDatetime && item.address?.zoneId
                      ? timeFromDatetime(item.dropoffDatetime, item.address.zoneId)
                      : ''
                  "
                />
              </label>

              <label
                class="wheels-itinerary-editor__date"
                v-if="!isLastStop(index)"
              >
                <span class="wheels-form-label">Pickup Date</span>
                <WheelsDatePicker
                  @change="handlePickupDateChange(item, $event)"
                  class="wheels-form-field wheels-itinerary-editor__date-picker"
                  :date="
                    item.pickupDatetime && item.address?.zoneId
                      ? dateFromDatetime(
                          item.pickupDatetime,
                          item.address?.zoneId,
                        )
                      : ''
                  "
                />
              </label>
              <label
                class="wheels-itinerary-editor__time"
                v-if="!isLastStop(index)"
              >
                <span class="wheels-form-label">Pickup Time</span>
                <WheelsTimeInput
                  @change="handlePickupTimeChange(item, $event)"
                  :value="
                    item.pickupDatetime && item.address?.zoneId
                      ? timeFromDatetime(
                          item.pickupDatetime,
                          item.address?.zoneId,
                        )
                      : ''
                  "
                />
              </label>
            </template>
            <template v-else>
              <div v-if="!isFirstStop(index) && !isLastStop(index)" class="wheels-itinerary-editor__divider"></div>
              <div class="wheels-itinerary-grid">
                <!-- Row 1: Headers -->
                <div></div>
                <label class="wheels-itinerary-grid__header">Date</label>
                <label class="wheels-itinerary-grid__header">Time</label>
                <div></div>
                
                <!-- Row 2: Dropoff -->
                <template v-if="!isFirstStop(index)">
                  <label class="wheels-itinerary-grid__label">Dropoff</label>
                  <WheelsDatePicker
                    @change="handleDropoffDateChange(item, $event)"
                    class="wheels-form-field"
                    :date="
                      item.dropoffDatetime && item.address?.zoneId
                        ? dateFromDatetime(
                            item.dropoffDatetime,
                            item.address?.zoneId,
                          )
                        : ''
                    "
                  />
                  <WheelsTimeInput
                    @change="handleDropoffTimeChange(item, $event)"
                    :value="
                      item.dropoffDatetime && item.address?.zoneId
                        ? timeFromDatetime(
                            item.dropoffDatetime,
                            item.address?.zoneId,
                          )
                        : ''
                    "
                  />
                  <WheelsButton 
                    v-if="!isLastStop(index)"
                    variant="plain" 
                    :disabled="!item.dropoffDatetime" 
                    @click="handleClearDropoffRow(item)"
                  >
                    Clear All
                  </WheelsButton>
                  <div v-else class="wheels-itinerary-grid__spacer"></div>
                </template>
                
                <!-- Row 3: Pickup -->
                <template v-if="!isLastStop(index)">
                  <label class="wheels-itinerary-grid__label">Pickup</label>
                  <WheelsDatePicker
                    @change="handlePickupDateChange(item, $event)"
                    class="wheels-form-field"
                    :date="
                      item.pickupDatetime && item.address?.zoneId
                        ? dateFromDatetime(
                            item.pickupDatetime,
                            item.address?.zoneId,
                          )
                        : ''
                    "
                  />
                  <WheelsTimeInput
                    @change="handlePickupTimeChange(item, $event)"
                    :value="
                      item.pickupDatetime && item.address?.zoneId
                        ? timeFromDatetime(
                            item.pickupDatetime,
                            item.address?.zoneId,
                          )
                        : ''
                    "
                  />
                    <WheelsButton
                      v-if="!isFirstStop(index)"
                      variant="plain"
                      @click="handleClearPickupRow(item)"
                      :disabled="!item.pickupDatetime"
                    >
                    Clear All
                    </WheelsButton>
                    <div v-else class="wheels-itinerary-grid__spacer"></div>
                </template>
              </div>
            </template>
          </fieldset>
          <div class="wheels-itinerary-editor__item-actions">
            <WheelsButton
              variant="plain"
              v-if="allowAddStop && !isLastStop(index)"
              @click="handleAddStopClick(index)"
              >+ Add Stop</WheelsButton
            >
            <WheelsButton
              variant="plain"
              v-if="
                allowRemoveStop && !isFirstStop(index) && !isLastStop(index)
              "
              @click="handleRemoveStopClick(index)"
              >Remove Stop</WheelsButton
            >
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<style scoped lang="scss">
.wheels-itinerary-editor ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.wheels-itinerary-editor ul li {
  margin-bottom: var(--spacing-8);
}

.wheels-itinerary-editor label > span:first-child {
  display: block;
}

.wheels-itinerary-editor__item-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-2);
  border: none;
  padding: 0;
  margin: 0 0 var(--spacing-2) 0;
}

.wheels-itinerary-editor__stop-type {
  grid-column: 1 / 3;
  display: flex;
  align-items: center;
  gap: var(--spacing-1);
  margin-bottom: var(--spacing-2);
  width: 100%;
}

.wheels-itinerary-editor__stop-type-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  line-height: 1;
  font-size: var(--text-xs);
  width: 24px;
  height: 24px;
  line-height: var(--spacing-4);
  color: white;
  background-color: var(--cu-gray-800);
  border-radius: 100%;
  padding: 0.5rem;
  font-size: 16px;
  margin-right: 8px;
}

.wheels-itinerary-editor__stop-typelabel {
  flex-grow: 1;
  font-size: 16px;
}

.wheels-itinerary-editor__stop-travel-time {
  align-self: end;
  text-align: right;
  font-size: var(--text-md);
  font-style: italic;
  margin-bottom: var(--spacing-2);
  color: var(--cu-gray-600);
}

.wheels-itinerary-editor__address-picker-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-1);
}

.wheels-itinerary-editor__address {
  grid-column: 1 / span 2;
}

.wheels-itinerary-editor__address-picker {
  width: 100%;
  display: block;
}

.wheels-itinerary-editor__date {
  grid-column: 1;
  .wheels-input {
    width: 100%;
  }
}

.wheels-itinerary-editor__date-picker {
  width: 100%;
  display: block;
}

.wheels-itinerary-editor__time {
  grid-column: 2;
}

.wheels-itinerary-editor__keep-on-site {
  grid-column: 1;
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-1);
}

.wheels-itinerary-editor__keep-on-site-label {
  grid-column: 2;
  text-align: right;
}

.wheels-itinerary-editor__keep-on-site-tooltip {
  .wheels-form-label {
    display: inline-flex;
    align-items: center;
    gap: var(--spacing-1);
  }

  svg {
    width: 1rem;
    height: 1rem;
    color: var(--cu-gray-800);
  }
}

.wheels-form-label {
  font-size: 14px;
}

.wheels-itinerary-editor__keep-on-site-tooltip {
  grid-column: 2;
}

.wheels-itinerary-editor__item-actions {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: var(--spacing-5);
}

.wheels-itinerary-editor__stop-header {
  grid-column: 1 / 3;
}

.wheels-itinerary-editor__error {
  color: var(--cu-error-primary);
  font-size: var(--text-base);
  white-space: nowrap;
  margin-left: var(--spacing-3);
}

.wheels-itinerary-editor__item-content--error {
  .wheels-itinerary-editor__stop-type {
    color: var(--cu-error-primary);
  }

  .wheels-itinerary-editor__stop-type-number {
    background-color: var(--cu-error-primary) !important;
    color: white;
  }

  .wheels-itinerary-editor__error {
    color: var(--cu-error-primary);
  }
}
.wheels-itinerary-grid {
  display: grid;
  grid-column: 1 / 3;
  grid-template-columns: auto 1fr 1fr auto;
  grid-template-rows: auto auto auto;
  gap: var(--spacing-3);
  align-items: center;
  margin: var(--spacing-3) var(--spacing-8) 0 var(--spacing-8);
  
}

.wheels-itinerary-editor__divider {
  grid-column: 1 / 3;
  height: 1px;
  background-color: var(--cu-gray-100);
  margin: var(--spacing-2) 0 0 0;
}

.wheels-itinerary-grid__header {
  font-weight: var(--font-weight-regular);
}

.wheels-itinerary-grid__label {
  font-weight: bold;
  font-size: var(--font-size-md);
  margin: 0 var(--spacing-5)
}

.wheels-itinerary-grid__clear-btn {
  padding: var(--spacing-2) var(--spacing-3);
  cursor: pointer;
}

.wheels-itinerary-grid .wheels-date-picker .wheels-form-field__input-wrapper {
  width: 100%;
}

.wheels-itinerary-grid .wheels-form-field {
  width: 100%;
  min-width: 0;
}

.wheels-itinerary-editor__item-content--v2 {
  border: 1px solid var(--cu-gray-200);
  border-radius: 8px;
  padding: var(--spacing-4);
}

.wheels-itinerary-grid__spacer {
  width: 67px;
  height: 23px;
}
</style>
